import { useMemo, useState } from 'react';
import {
  AlertCircle,
  Archive,
  BookOpen,
  ChevronLeft,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import {
  useArchiveQuestion,
  useCreateQuestion,
  useQuestionBank,
  useRestoreQuestion,
  useUpdateAndPublishQuestion,
} from '@/hooks/forms-engine/useQuestionBank';
import {
  currentPublishedQuestion,
  isQuestionSupportedByLegacyBuilder,
} from '@/lib/forms/question-bank';
import type {
  CreateQuestionInput,
  CreateQuestionVersionInput,
  FormItemConfig,
  QuestionDefinition,
  QuestionVersion,
} from '@/types/formsEngine';

interface QuestionBankPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (question: QuestionDefinition) => void;
  allowPhoto?: boolean;
  publicOnly?: boolean;
  allowedTypes?: BuilderQuestionType[];
}

type PickerMode = 'browse' | 'create' | 'edit';
type BankStatus = 'ACTIVE' | 'ARCHIVED';
type BuilderQuestionType = QuestionVersion['type'];

const QUESTION_TYPES: Array<{
  value: BuilderQuestionType;
  label: string;
  description: string;
}> = [
  { value: 'TEXT', label: 'Text', description: 'A written response' },
  { value: 'NUMBER', label: 'Number', description: 'A numeric value' },
  { value: 'MEASUREMENT', label: 'Measurement', description: 'A value plus a unit' },
  { value: 'CHECKBOX', label: 'Checkbox', description: 'A single confirmation' },
  { value: 'YES_NO', label: 'Yes / No', description: 'Two clear choices' },
  { value: 'PASS_FAIL', label: 'Pass / Fail', description: 'An inspection result' },
  { value: 'SINGLE_SELECT', label: 'Single select', description: 'Choose one option' },
  { value: 'MULTI_SELECT', label: 'Multi select', description: 'Choose several options' },
  { value: 'DATE', label: 'Date', description: 'A calendar date' },
  { value: 'PHOTO', label: 'Photo', description: 'Upload image evidence' },
  { value: 'SIGNATURE', label: 'Signature', description: 'A drawn signature with signer' },
  { value: 'BARCODE_SCAN', label: 'Barcode scan', description: 'Scan an equipment code' },
];

const PUBLIC_QUESTION_TYPES = new Set<BuilderQuestionType>([
  'TEXT',
  'NUMBER',
  'CHECKBOX',
  'YES_NO',
  'SINGLE_SELECT',
  'MULTI_SELECT',
  'DATE',
]);

export function QuestionBankPicker({
  open,
  onOpenChange,
  onSelect,
  allowPhoto = true,
  publicOnly = false,
  allowedTypes,
}: QuestionBankPickerProps) {
  const [mode, setMode] = useState<PickerMode>('browse');
  const [bankStatus, setBankStatus] = useState<BankStatus>('ACTIVE');
  const [search, setSearch] = useState('');
  const [editingQuestion, setEditingQuestion] = useState<QuestionDefinition | null>(null);
  const activeQuestionQuery = useQuestionBank({ status: 'ACTIVE' });
  const archivedQuestionQuery = useQuestionBank({ status: 'ARCHIVED' });
  const archiveQuestion = useArchiveQuestion();
  const restoreQuestion = useRestoreQuestion();
  const questionQuery = bankStatus === 'ACTIVE' ? activeQuestionQuery : archivedQuestionQuery;
  const compatibleQuestions = useMemo(
    () =>
      (questionQuery.data ?? []).filter((question) => {
        if (!isQuestionSupportedByLegacyBuilder(question)) return false;
        const type = currentPublishedQuestion(question)?.type;
        if (!type) return false;
        if (allowedTypes && !allowedTypes.includes(type)) return false;
        if (publicOnly && !PUBLIC_QUESTION_TYPES.has(type)) return false;
        return allowPhoto || type !== 'PHOTO';
      }),
    [allowPhoto, allowedTypes, publicOnly, questionQuery.data],
  );
  const filteredQuestions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return compatibleQuestions;
    return compatibleQuestions.filter((question) => {
      const version = currentPublishedQuestion(question);
      return [
        question.name,
        question.key,
        version?.prompt ?? '',
        version?.instructions ?? '',
        ...question.tags,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [compatibleQuestions, search]);

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setMode('browse');
      setBankStatus('ACTIVE');
      setSearch('');
      setEditingQuestion(null);
    }
  }

  function choose(question: QuestionDefinition) {
    onSelect(question);
    handleOpenChange(false);
  }

  function edit(question: QuestionDefinition) {
    setEditingQuestion(question);
    setMode('edit');
  }

  function returnToBank() {
    setMode('browse');
    setEditingQuestion(null);
  }

  const editorOpen = mode === 'create' || (mode === 'edit' && editingQuestion);
  const activeCount = activeQuestionQuery.data?.length ?? 0;
  const archivedCount = archivedQuestionQuery.data?.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[min(720px,90vh)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b px-5 py-4">
          <div className="flex items-start gap-3">
            {editorOpen ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Back to question bank"
                onClick={returnToBank}
              >
                <ChevronLeft className="size-4" />
              </Button>
            ) : (
              <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/50">
                <BookOpen className="size-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <DialogTitle>
                {mode === 'create'
                  ? 'Create reusable question'
                  : mode === 'edit'
                    ? 'Edit reusable question'
                    : 'Question bank'}
              </DialogTitle>
              <DialogDescription>
                {mode === 'create'
                  ? 'Create it once, then pin this published version into the form.'
                  : mode === 'edit'
                    ? 'Publish a new version. Existing forms stay pinned to the version they use.'
                    : 'Choose a published question to add to this section.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {editorOpen ? (
          <QuestionEditorPanel
            key={editingQuestion?.id ?? 'new-question'}
            question={editingQuestion ?? undefined}
            allowPhoto={allowPhoto}
            publicOnly={publicOnly}
            allowedTypes={allowedTypes}
            onCancel={returnToBank}
            onCreated={choose}
            onUpdated={returnToBank}
          />
        ) : (
          <>
            <div className="border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search questions, instructions, or tags..."
                    className="h-9 pl-8"
                  />
                </div>
                <Button type="button" size="sm" onClick={() => setMode('create')}>
                  <Plus className="mr-1.5 size-3.5" />
                  New question
                </Button>
              </div>
              <div className="mt-3 flex items-center gap-1">
                <Button
                  type="button"
                  variant={bankStatus === 'ACTIVE' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setBankStatus('ACTIVE')}
                >
                  Active
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{activeCount}</span>
                </Button>
                <Button
                  type="button"
                  variant={bankStatus === 'ARCHIVED' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setBankStatus('ARCHIVED')}
                >
                  Archived
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{archivedCount}</span>
                </Button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {questionQuery.isLoading ? (
                <div className="flex min-h-56 items-center justify-center">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : questionQuery.isError ? (
                <div className="flex min-h-56 flex-col items-center justify-center gap-3 text-center">
                  <AlertCircle className="size-7 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Question bank could not be loaded</p>
                    <p className="text-xs text-muted-foreground">
                      Check your connection and try again.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void questionQuery.refetch()}
                  >
                    Try again
                  </Button>
                </div>
              ) : filteredQuestions.length === 0 ? (
                <div className="flex min-h-56 flex-col items-center justify-center gap-3 px-6 text-center">
                  <div className="flex size-10 items-center justify-center rounded-full bg-muted">
                    <BookOpen className="size-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {compatibleQuestions.length === 0
                        ? bankStatus === 'ARCHIVED'
                          ? 'No archived questions'
                          : 'No reusable questions yet'
                        : 'No questions match that search'}
                    </p>
                    <p className="mt-0.5 max-w-sm text-xs text-muted-foreground">
                      {compatibleQuestions.length === 0
                        ? bankStatus === 'ARCHIVED'
                          ? 'Archived questions will appear here and can be restored.'
                          : 'Create the first question and it will be available in every form.'
                        : 'Try a question label, instruction, or tag.'}
                    </p>
                  </div>
                  {compatibleQuestions.length === 0 && bankStatus === 'ACTIVE' ? (
                    <Button type="button" size="sm" onClick={() => setMode('create')}>
                      <Plus className="mr-1.5 size-3.5" />
                      Create question
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredQuestions.map((question) => {
                    const version = currentPublishedQuestion(question);
                    if (!version) return null;
                    return (
                      <div
                        key={question.id}
                        className="group flex items-start rounded-md border border-transparent transition-colors hover:border-border hover:bg-muted/40"
                      >
                        <button
                          type="button"
                          disabled={bankStatus === 'ARCHIVED'}
                          onClick={() => choose(question)}
                          className="flex min-w-0 flex-1 items-start gap-3 px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-default"
                        >
                          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border bg-background text-[10px] font-semibold text-muted-foreground">
                            {typeAbbreviation(version.type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{version.prompt}</p>
                            {version.instructions ? (
                              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                {version.instructions}
                              </p>
                            ) : null}
                            <div className="mt-1.5 flex flex-wrap items-center gap-1">
                              <Badge variant="secondary" className="text-[10px]">
                                {typeLabel(version.type)}
                              </Badge>
                              <span className="text-[10px] text-muted-foreground">
                                v{version.version}
                              </span>
                              {question.usageCount ? (
                                <span className="text-[10px] text-muted-foreground">
                                  · used in {question.usageCount}{' '}
                                  {question.usageCount === 1 ? 'form' : 'forms'}
                                </span>
                              ) : null}
                              {question.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          {bankStatus === 'ACTIVE' ? (
                            <Plus className="mt-2 size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                          ) : null}
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              className="mr-2 mt-2 shrink-0"
                              aria-label={`Actions for ${version.prompt}`}
                            >
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {bankStatus === 'ACTIVE' ? (
                              <>
                                <DropdownMenuItem onSelect={() => edit(question)}>
                                  <Pencil className="size-3.5" />
                                  Edit and publish new version
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  variant="destructive"
                                  disabled={archiveQuestion.isPending}
                                  onSelect={() => archiveQuestion.mutate(question.id)}
                                >
                                  <Archive className="size-3.5" />
                                  Archive
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem
                                disabled={restoreQuestion.isPending}
                                onSelect={() => restoreQuestion.mutate(question.id)}
                              >
                                <RotateCcw className="size-3.5" />
                                Restore
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuestionEditorPanel({
  question,
  allowPhoto,
  publicOnly,
  allowedTypes,
  onCancel,
  onCreated,
  onUpdated,
}: {
  question?: QuestionDefinition;
  allowPhoto: boolean;
  publicOnly: boolean;
  allowedTypes?: BuilderQuestionType[];
  onCancel: () => void;
  onCreated: (question: QuestionDefinition) => void;
  onUpdated: () => void;
}) {
  const create = useCreateQuestion();
  const update = useUpdateAndPublishQuestion();
  const currentVersion = question ? currentPublishedQuestion(question) : undefined;
  const availableQuestionTypes = QUESTION_TYPES.filter(
    (questionType) =>
      (!publicOnly || PUBLIC_QUESTION_TYPES.has(questionType.value)) &&
      (allowPhoto || questionType.value !== 'PHOTO') &&
      (!allowedTypes || allowedTypes.includes(questionType.value)),
  );
  const [name, setName] = useState(question?.name ?? '');
  const [prompt, setPrompt] = useState(currentVersion?.prompt ?? '');
  const [instructions, setInstructions] = useState(currentVersion?.instructions ?? '');
  const [type, setType] = useState<BuilderQuestionType>(
    currentVersion?.type ?? availableQuestionTypes[0]?.value ?? 'TEXT',
  );
  const [options, setOptions] = useState<string[]>(
    currentVersion?.config?.options ?? currentVersion?.config?.units ?? [],
  );
  const [optionDraft, setOptionDraft] = useState('');
  const [minimum, setMinimum] = useState(stringValue(currentVersion?.config?.min));
  const [maximum, setMaximum] = useState(stringValue(currentVersion?.config?.max));
  const [maxLength, setMaxLength] = useState(stringValue(currentVersion?.config?.maxLength));
  const [signerRole, setSignerRole] = useState(currentVersion?.config?.signerRole ?? '');
  const [barcodeFormat, setBarcodeFormat] = useState<NonNullable<FormItemConfig['format']>>(
    currentVersion?.config?.format ?? 'ANY',
  );
  const [allowPast, setAllowPast] = useState(currentVersion?.config?.allowPast ?? true);
  const [allowFuture, setAllowFuture] = useState(currentVersion?.config?.allowFuture ?? true);
  const [photoRequired, setPhotoRequired] = useState(
    currentVersion?.evidence?.['photoRequired'] === true,
  );
  const [minPhotos, setMinPhotos] = useState(
    stringValue(currentVersion?.evidence?.['minPhotos']) || '1',
  );
  const [maxPhotos, setMaxPhotos] = useState(
    stringValue(currentVersion?.evidence?.['maxPhotos'] ?? currentVersion?.config?.maxPhotos),
  );
  const [tags, setTags] = useState(question?.tags.join(', ') ?? '');
  const needsOptions = type === 'SINGLE_SELECT' || type === 'MULTI_SELECT';
  const needsUnits = type === 'MEASUREMENT';
  const numeric = type === 'NUMBER' || type === 'MEASUREMENT';
  const minimumValue = optionalNumber(minimum);
  const maximumValue = optionalNumber(maximum);
  const maxLengthValue = optionalInteger(maxLength);
  const minPhotosValue = optionalInteger(minPhotos);
  const maxPhotosValue = optionalInteger(maxPhotos);
  const hasPhotoDefaults = allowPhoto && (type === 'PHOTO' || photoRequired);
  const numericRangeValid =
    minimumValue !== null &&
    maximumValue !== null &&
    (minimumValue === undefined || maximumValue === undefined || minimumValue <= maximumValue);
  const maxLengthValid =
    maxLengthValue !== null && (maxLengthValue === undefined || maxLengthValue > 0);
  const photoLimitsValid =
    !hasPhotoDefaults ||
    (minPhotosValue !== null &&
      minPhotosValue !== undefined &&
      minPhotosValue > 0 &&
      maxPhotosValue !== null &&
      (maxPhotosValue === undefined || maxPhotosValue >= minPhotosValue));
  const canSave =
    (Boolean(question) || name.trim().length > 0) &&
    prompt.trim().length > 0 &&
    (!needsOptions || options.length >= 2) &&
    (!needsUnits || options.length >= 1) &&
    (!numeric || numericRangeValid) &&
    (type !== 'TEXT' || maxLengthValid) &&
    (type !== 'DATE' || allowPast || allowFuture) &&
    photoLimitsValid;

  function changeType(nextType: BuilderQuestionType) {
    setType(nextType);
    setOptions([]);
    setOptionDraft('');
    setMinimum('');
    setMaximum('');
    setMaxLength('');
    setSignerRole('');
    setBarcodeFormat('ANY');
    setAllowPast(true);
    setAllowFuture(true);
    setPhotoRequired(false);
    setMinPhotos('1');
    setMaxPhotos(nextType === 'PHOTO' ? '5' : '');
  }

  function addOption() {
    const next = optionDraft.trim();
    if (!next || options.includes(next)) return;
    setOptions((current) => [...current, next]);
    setOptionDraft('');
  }

  function submit() {
    if (!canSave) return;
    const config: FormItemConfig = {};
    if (needsOptions) config.options = options;
    if (needsUnits) config.units = options;
    if (numeric) {
      if (minimumValue !== undefined && minimumValue !== null) config.min = minimumValue;
      if (maximumValue !== undefined && maximumValue !== null) config.max = maximumValue;
    }
    if (type === 'TEXT' && maxLengthValue !== undefined && maxLengthValue !== null) {
      config.maxLength = maxLengthValue;
    }
    if (type === 'SIGNATURE' && signerRole.trim()) config.signerRole = signerRole.trim();
    if (type === 'BARCODE_SCAN') config.format = barcodeFormat;
    if (type === 'DATE') {
      config.allowPast = allowPast;
      config.allowFuture = allowFuture;
    }
    if (type === 'PHOTO' && maxPhotosValue !== undefined && maxPhotosValue !== null) {
      config.maxPhotos = maxPhotosValue;
    }

    const versionInput: CreateQuestionVersionInput = {
      prompt: prompt.trim(),
      instructions: instructions.trim() || null,
      type,
      config: Object.keys(config).length > 0 ? config : null,
      evidence: hasPhotoDefaults
        ? {
            photoRequired: true,
            minPhotos: minPhotosValue ?? 1,
            ...(maxPhotosValue !== undefined && maxPhotosValue !== null
              ? { maxPhotos: maxPhotosValue }
              : {}),
          }
        : null,
    };
    if (question) {
      update.mutate({ questionId: question.id, input: versionInput }, { onSuccess: onUpdated });
      return;
    }
    const input: CreateQuestionInput = {
      name: name.trim(),
      ...versionInput,
      tags: splitList(tags),
      publish: true,
    };
    create.mutate(input, { onSuccess: onCreated });
  }

  return (
    <>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
        {question && currentVersion ? (
          <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/20 px-4 py-3">
            <div>
              <p className="text-xs font-medium">Publishing a new version</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Forms already using v{currentVersion.version} will not change.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2 font-mono text-xs">
              <Badge variant="secondary">v{currentVersion.version}</Badge>
              <span className="text-muted-foreground">→</span>
              <Badge>v{currentVersion.version + 1}</Badge>
            </div>
          </div>
        ) : null}

        <div className={`grid gap-4 ${question ? '' : 'sm:grid-cols-2'}`}>
          {!question ? (
            <div className="space-y-1.5">
              <Label htmlFor="question-library-name">Library name</Label>
              <Input
                id="question-library-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Arrival condition"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                A short internal name for finding this question.
              </p>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label>Answer type</Label>
            <Select
              value={type}
              disabled={Boolean(question)}
              onValueChange={(value) => changeType(value as BuilderQuestionType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableQuestionTypes.map((questionType) => (
                  <SelectItem key={questionType.value} value={questionType.value}>
                    <span>{questionType.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {question
                ? 'The answer type stays fixed so historical answers remain comparable.'
                : QUESTION_TYPES.find((candidate) => candidate.value === type)?.description}
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="question-prompt">Question</Label>
          <Input
            id="question-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="What condition was the work area in when you arrived?"
            autoFocus={Boolean(question)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="question-instructions">Instructions</Label>
          <Textarea
            id="question-instructions"
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            placeholder="Add context that helps the person answer consistently."
            rows={3}
          />
        </div>

        <div className="rounded-lg border bg-muted/10">
          <div className="border-b px-4 py-3">
            <p className="text-sm font-medium">Reusable defaults</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              These settings follow the question into a form. Local completion rules can still be
              adjusted there.
            </p>
          </div>
          <div className="space-y-4 p-4">
            {needsOptions || needsUnits ? (
              <QuestionListDefaults
                label={needsUnits ? 'Allowed units' : 'Options'}
                values={options}
                draft={optionDraft}
                placeholder={needsUnits ? 'PSI' : 'Excellent'}
                minimum={needsUnits ? 1 : 2}
                onDraftChange={setOptionDraft}
                onAdd={addOption}
                onRemove={(value) =>
                  setOptions((current) => current.filter((candidate) => candidate !== value))
                }
              />
            ) : null}

            {numeric ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <QuestionNumberDefault
                  label="Minimum value"
                  value={minimum}
                  onChange={setMinimum}
                  placeholder="No minimum"
                />
                <QuestionNumberDefault
                  label="Maximum value"
                  value={maximum}
                  onChange={setMaximum}
                  placeholder="No maximum"
                />
              </div>
            ) : null}

            {type === 'TEXT' ? (
              <QuestionNumberDefault
                label="Maximum characters"
                value={maxLength}
                onChange={setMaxLength}
                placeholder="No limit"
                integer
                min={1}
              />
            ) : null}

            {type === 'DATE' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <QuestionSwitchDefault
                  label="Allow past dates"
                  description="The answer may be before today."
                  checked={allowPast}
                  onCheckedChange={setAllowPast}
                />
                <QuestionSwitchDefault
                  label="Allow future dates"
                  description="The answer may be after today."
                  checked={allowFuture}
                  onCheckedChange={setAllowFuture}
                />
              </div>
            ) : null}

            {type === 'SIGNATURE' ? (
              <div className="space-y-1.5">
                <Label htmlFor="question-signer-role">Signer role</Label>
                <Input
                  id="question-signer-role"
                  value={signerRole}
                  onChange={(event) => setSignerRole(event.target.value)}
                  placeholder="Customer, technician"
                />
              </div>
            ) : null}

            {type === 'BARCODE_SCAN' ? (
              <div className="space-y-1.5">
                <Label>Barcode format</Label>
                <Select
                  value={barcodeFormat}
                  onValueChange={(value) =>
                    setBarcodeFormat(value as NonNullable<FormItemConfig['format']>)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['ANY', 'UPC', 'QR', 'CODE128'].map((format) => (
                      <SelectItem key={format} value={format}>
                        {format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {allowPhoto && type !== 'PHOTO' ? (
              <QuestionSwitchDefault
                label="Require photo evidence"
                description="A photo will be requested with this answer by default."
                checked={photoRequired}
                onCheckedChange={setPhotoRequired}
              />
            ) : null}

            {hasPhotoDefaults ? (
              <div className="grid gap-3 border-t pt-4 sm:grid-cols-2">
                <QuestionNumberDefault
                  label="Minimum photos"
                  value={minPhotos}
                  onChange={setMinPhotos}
                  min={1}
                  integer
                />
                <QuestionNumberDefault
                  label="Maximum photos"
                  value={maxPhotos}
                  onChange={setMaxPhotos}
                  placeholder="No maximum"
                  min={1}
                  integer
                />
              </div>
            ) : null}

            {!needsOptions &&
            !needsUnits &&
            !numeric &&
            type !== 'TEXT' &&
            type !== 'DATE' &&
            type !== 'SIGNATURE' &&
            type !== 'BARCODE_SCAN' &&
            !allowPhoto ? (
              <p className="text-xs text-muted-foreground">
                This answer type has no additional reusable settings.
              </p>
            ) : null}
          </div>
        </div>

        {!question ? (
          <div className="space-y-1.5">
            <Label htmlFor="question-tags">Tags</Label>
            <Input
              id="question-tags"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
              placeholder="arrival, inspection, customer"
            />
            <p className="text-xs text-muted-foreground">
              Optional comma-separated terms make the bank easier to search.
            </p>
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-2 border-t px-5 py-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!canSave || create.isPending || update.isPending}
          onClick={submit}
        >
          {create.isPending || update.isPending ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : question ? (
            <Pencil className="mr-1.5 size-3.5" />
          ) : (
            <Plus className="mr-1.5 size-3.5" />
          )}
          {question ? `Publish v${question.currentVersion + 1}` : 'Create and add'}
        </Button>
      </div>
    </>
  );
}

function QuestionListDefaults({
  label,
  values,
  draft,
  placeholder,
  minimum,
  onDraftChange,
  onAdd,
  onRemove,
}: {
  label: string;
  values: string[];
  draft: string;
  placeholder: string;
  minimum: number;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span className="text-[10px] text-muted-foreground">
          {values.length} · minimum {minimum}
        </span>
      </div>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value) => (
            <Badge key={value} variant="secondary" className="gap-1 pr-1">
              {value}
              <button
                type="button"
                aria-label={`Remove ${value}`}
                className="rounded p-0.5 hover:bg-muted"
                onClick={() => onRemove(value)}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onAdd();
            }
          }}
          placeholder={placeholder}
        />
        <Button type="button" variant="outline" onClick={onAdd}>
          Add
        </Button>
      </div>
    </div>
  );
}

function QuestionNumberDefault({
  label,
  value,
  onChange,
  placeholder,
  integer = false,
  min,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  integer?: boolean;
  min?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        step={integer ? 1 : 'any'}
        min={min}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function QuestionSwitchDefault({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-md border bg-background p-3">
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
        className="mt-0.5"
      />
      <span className="min-w-0">
        <span className="block text-xs font-medium">{label}</span>
        <span className="mt-0.5 block text-[10px] leading-relaxed text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

function splitList(value: string): string[] {
  return [
    ...new Set(
      value
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean),
    ),
  ];
}

function stringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function optionalNumber(value: string): number | undefined | null {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function optionalInteger(value: string): number | undefined | null {
  const parsed = optionalNumber(value);
  if (parsed === undefined || parsed === null) return parsed;
  return Number.isInteger(parsed) ? parsed : null;
}

function typeAbbreviation(type: string): string {
  switch (type) {
    case 'SINGLE_SELECT':
      return '1×';
    case 'MULTI_SELECT':
      return 'N×';
    case 'CHECKBOX':
      return '✓';
    case 'PHOTO':
      return 'IMG';
    default:
      return 'TXT';
  }
}

function typeLabel(type: string): string {
  return QUESTION_TYPES.find((candidate) => candidate.value === type)?.label ?? type;
}
